import { Box, Collapse, Typography } from '@mui/material';
import { useState, type ReactNode } from 'react';
import { osirisTokens } from '@/theme/tokens';
import { Icon } from './Icon';

interface AccordionPanelProps {
  title: ReactNode;
  children: ReactNode;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onToggle?: (next: boolean) => void;
  className?: string;
  right?: ReactNode;
  summary?: ReactNode;
  /** Show the expand/collapse arrow (session-page panels hide it per design). */
  chevron?: boolean;
  /** Static section: header is not a toggle and the content is always open (Figma 28786:75354). */
  collapsible?: boolean;
}

export function AccordionPanel({
  title,
  children,
  defaultExpanded = false,
  expanded,
  onToggle,
  className,
  right,
  summary,
  chevron = true,
  collapsible = true,
}: AccordionPanelProps) {
  const [internal, setInternal] = useState(defaultExpanded);
  const isControlled = expanded !== undefined;
  const open = collapsible ? (isControlled ? expanded : internal) : true;

  const toggle = () => {
    const next = !open;
    if (!isControlled) setInternal(next);
    onToggle?.(next);
  };

  return (
    <Box
      className={`accordion-panel${className ? ' ' + className : ''}`}
      sx={{ borderBottom: `1px solid ${osirisTokens.border.secondary}` }}
    >
      <Box
        component={collapsible ? 'button' : 'div'}
        // buttons default to type="submit" — inside a <form> (drawer forms)
        // a header click would submit instead of toggling
        type={collapsible ? 'button' : undefined}
        onClick={collapsible ? toggle : undefined}
        aria-expanded={collapsible ? open : undefined}
        className="accordion-panel-header"
        sx={{
          all: 'unset',
          boxSizing: 'border-box',
          cursor: collapsible ? 'pointer' : 'default',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          height: 40,
          pl: 2,
          pr: 1.25,
          color: osirisTokens.text.tertiary,
          ...(collapsible && { '&:hover': { color: osirisTokens.text.primary } }),
        }}
      >
        <Typography
          sx={{
            fontFamily: osirisTokens.fonts.display,
            fontSize: 13,
            fontWeight: 400,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'inherit',
          }}
        >
          {title}
        </Typography>
        {!open && summary && (
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              pl: 1.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontFamily: osirisTokens.fonts.mono,
              fontSize: 11,
              color: osirisTokens.text.muted,
              textAlign: 'right',
              // Keep the summary clear of the panel edge, chevron or not
            }}
          >
            {summary}
          </Box>
        )}
        {right && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pr: chevron ? 1 : 0 }}>
            {right}
          </Box>
        )}
        {chevron && collapsible && (
          <Icon
            name="arrow-down-S"
            size={20}
            className="accordion-panel-chevron"
            sx={{
              // Figma Header-accordion: 20px glyph, 10px from the panel edge
              color: 'inherit',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 200ms ease',
            }}
          />
        )}
      </Box>
      <Collapse in={open} timeout={150} unmountOnExit>
        <Box className="accordion-panel-content" sx={{ px: 2, pb: 2 }}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}
